import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Layout, Menu, Button, Space, theme, MenuProps, Grid, Input } from 'antd';
import { useNavigate, useLocation, Outlet, useMatches } from 'react-router-dom';
import AccentButton from '../AccentButton/AccentButton';
import IconButton from '../IconButton/IconButton';
import BrandedButton from '../BrandedButton/BrandedButton';
import AccentButtonDark from '../AccentButtonDark/AccentButtonDark';
import FooterNav from '../Footer/Footer';
import { ShowsDrawer } from '../ShowsDrawer/ShowsDrawer';
import { MiniPlayer } from '../MiniPlayer/MiniPlayer';
import { useShows } from '../../api/queries';
import { Show } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import {
  MenuIcon,
  HomeIcon,
  CalendarIcon,
  UserGroupIcon,
  UserIcon,
  LocationMarkerIcon,
  ChevronLeftIcon,
  SpecLogo,
  UserCircleIcon,
  CogIcon,
  XIcon,
  DotsVerticalIcon,
  SunglassLogo,
  LinkIcon,
} from '../icons';
import './MainLayout.css';

import { shadows } from '../../theme/tokens/shadows';
import { blur } from '../../theme/tokens/blur';
import { motion } from '../../theme/tokens/motion';

export interface HeaderConfig {
  showHamburger?: boolean;
  showClose?: boolean;
  showBack?: boolean;
  showLogo?: boolean;
  showSearch?: boolean;
  showMore?: boolean;
  showGradient?: boolean;
}

const defaultHeaderConfig: HeaderConfig = {
  showHamburger: true,
  showClose: false,
  showBack: false,
  showLogo: false,
  showSearch: true,
  showMore: false,
  showGradient: true,
};

interface HeaderContextType {
  config: HeaderConfig;
  setConfig: (config: Partial<HeaderConfig>) => void;
  resetConfig: () => void;
  onMoreClick?: () => void;
  setOnMoreClick: (callback: (() => void) | undefined) => void;
  rightExtra?: React.ReactNode;
  setRightExtra: (node: React.ReactNode | undefined) => void;
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  rightDrawerOpen: boolean;
  setRightDrawerOpen: (open: boolean) => void;
  selectedDate: string | null;
  setSelectedDate: (date: string | null) => void;
  focusedDrawerShow: Show | null;
  setFocusedDrawerShow: (show: Show | null) => void;
}

const HeaderContext = React.createContext<HeaderContextType | undefined>(undefined);

export const useHeader = () => {
  const context = React.useContext(HeaderContext);
  if (!context) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector('.main-content')?.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const { Header, Sider, Content } = Layout;
const { useToken } = theme;

const ANCHOR_PAGES = ['/home', '/chat', '/profile', '/fan-profile', '/musician-profile'];
const DIRECTORY_PAGES = ['/shows', '/bands', '/musicians', '/venues'];

function MainLayout() {
  const [collapsed, setCollapsed] = useState(true);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [focusedDrawerShow, setFocusedDrawerShow] = useState<Show | null>(null);
  const { user, signOut } = useAuth();
  const isAuthenticated = !!user;
  const isAdmin = user?.claims?.roles?.includes('admin') ?? false;
  const setIsAuthenticated = useCallback(() => {}, []); // No-op for backwards compatibility
  const matches = useMatches();
  const [headerConfig, setHeaderConfig] = useState<HeaderConfig>(defaultHeaderConfig);
  const onMoreClickRef = useRef<(() => void) | undefined>(undefined);
  const [rightExtra, setRightExtra] = useState<React.ReactNode | undefined>(undefined);
  const lastAnchorPageRef = useRef<string>('/home');
  const navDepthRef = useRef(0);
  const lastLocationKeyRef = useRef<string | null>(null);
  const searchInputRef = useRef<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useToken() as { token: any };
  const currentPage = location.pathname.split('/')[1] || 'home';
  const animateSearch = (location.state as any)?.animateSearch;

  // Determine whether to show the footer from route handle (default: true)
  const showFooter = (() => {
    const routeMatch = [...matches].reverse().find(m => m.handle);
    return (routeMatch?.handle as any)?.showFooter !== false;
  })();

  // Shows drawer data (only fetch on home page)
  const isHomePage = location.pathname === '/home';
  const { data: shows = [] } = useShows();

  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    shows.forEach(show => {
      const dateKey = show.date.split('T')[0];
      dates.add(dateKey);
    });
    return Array.from(dates).sort();
  }, [shows]);

  // Set initial date when drawer opens
  useEffect(() => {
    if (rightDrawerOpen && !selectedDate && availableDates.length > 0) {
      const today = new Date().toISOString().split('T')[0];
      const futureDate = availableDates.find(d => d >= today);
      setSelectedDate(futureDate || availableDates[0]);
    }
  }, [rightDrawerOpen, selectedDate, availableDates]);

  const handleSearchClick = () => {
    navigate('/shows', { state: { animateSearch: true } });
  };

  const handleBackClick = () => {
    const currentPath = location.pathname;
    
    // For directory pages: go to last anchor page
    if (DIRECTORY_PAGES.includes(currentPath)) {
      navigate(lastAnchorPageRef.current);
    } else if (navDepthRef.current > 0) {
      // User has in-app history — regular browser back
      navigate(-1);
    } else {
      // No in-app history (direct/shared link) — go home
      navigate('/home');
    }
  };

  const setConfig = (newConfig: Partial<HeaderConfig>) => {
    setHeaderConfig(prev => ({ ...prev, ...newConfig }));
  };

  const resetConfig = () => {
    // Find the current route's config from matches
    const routeMatch = [...matches]
      .reverse()
      .find(m => m.handle && (m.handle as any).headerConfig);

    const config = (routeMatch?.handle as any)?.headerConfig || {};
    setHeaderConfig({ ...defaultHeaderConfig, ...config });
  };

  const setOnMoreClick = useCallback((callback: (() => void) | undefined) => {
    onMoreClickRef.current = callback;
  }, []);

  const handleMoreClick = useCallback(() => {
    onMoreClickRef.current?.();
  }, []);

  useEffect(() => {
    resetConfig();
  }, [matches]);

  useEffect(() => {
    setCollapsed(true);
    setRightDrawerOpen(false);
    setSearchQuery('');
  }, [location.pathname]);

  useEffect(() => {
    if (animateSearch) {
      if (currentPage !== 'home' && searchInputRef.current) {
        searchInputRef.current.blur();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            searchInputRef.current?.focus();
          });
        });
      }
      const timer = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} });
      }, motion.durationMid);
      return () => clearTimeout(timer);
    }
  }, [animateSearch, location.pathname, navigate, currentPage]);

  useEffect(() => {
    if (!collapsed) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [collapsed]);

  useEffect(() => {
    if (ANCHOR_PAGES.includes(location.pathname)) {
      lastAnchorPageRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Track how many in-app navigations have occurred so we know
  // whether navigate(-1) is safe or the user arrived via direct link.
  // Compare location.key to avoid double-counting from StrictMode re-fires.
  useEffect(() => {
    const key = location.key;
    if (lastLocationKeyRef.current !== null && key !== lastLocationKeyRef.current) {
      navDepthRef.current += 1;
    }
    lastLocationKeyRef.current = key;
  }, [location]);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    const targetPath = `/${e.key}`;

    // If already on this page, just close the sider
    if (location.pathname === targetPath) {
      setCollapsed(true);
      return;
    }

    const isCurrentDirectory = DIRECTORY_PAGES.includes(location.pathname);
    const isTargetDirectory = DIRECTORY_PAGES.includes(targetPath);
    const animateState = DIRECTORY_PAGES.includes(targetPath) ? { animateSearch: true } : {};
    
    // If navigating from one directory page to another, replace history
    if (isCurrentDirectory && isTargetDirectory) {
      navigate(targetPath, { replace: true, state: animateState });
    } else {
      navigate(targetPath, { state: animateState });
    }
  };

  return (
    <HeaderContext.Provider value={{ config: headerConfig, setConfig, resetConfig, onMoreClick: onMoreClickRef.current, setOnMoreClick, rightExtra, setRightExtra, isAuthenticated, setIsAuthenticated, searchQuery, setSearchQuery, rightDrawerOpen, setRightDrawerOpen, selectedDate, setSelectedDate, focusedDrawerShow, setFocusedDrawerShow }}>
      <Layout className="main-layout-container" style={{ background: token.colorBgLayout }}>
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          collapsedWidth={0}
          width={280}
          className="main-layout-sider"
          style={{
            background: token.colorBgBase,
            boxShadow: shadows.specLight4
          }}
        >
          <div
            className="sider-header"
            style={{
              height: token.controlHeightLG,
              margin: `${token.marginLG}px ${token.padding}px`,
              color: token.colorText,
            }}
          >
            <IconButton
              icon={<ChevronLeftIcon size="m" tone="white" />}
              onClick={() => setCollapsed(true)}
              aria-label="Close sidebar"
            />
            <div className="sider-logo">
              <div onClick={() => { setCollapsed(true); navigate('/home'); }} style={{ cursor: 'pointer' }} className="sider-logo-group">
                <SpecLogo />
                <div className="sider-city-label">Tulsa</div>
              </div>
              <img src="/images/app-icon.png" alt="Spec" className="sider-app-icon" onClick={() => { setCollapsed(true); navigate('/chat'); }} style={{ cursor: 'pointer' }} />
            </div>
          </div>

          <div className="sider-info-card">
            <p className="sider-info-card-text">Spec is a local live music community for fans, musicians, bands, and&nbsp;venues</p>
          </div>

          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[currentPage]}
            onClick={handleMenuClick}
            className="sider-menu"
            style={{ background: token.colorBgBase }}
            items={[
              {
                key: 'home',
                icon: <HomeIcon size="m" tone={currentPage === 'home' ? 'white' : 'darkGrey'} />,
                label: 'Home',
              },
              {
                key: 'shows',
                icon: <CalendarIcon size="m" tone={currentPage === 'shows' ? 'white' : 'darkGrey'} />,
                label: 'Shows',
              },
              {
                key: 'bands',
                icon: <UserGroupIcon size="m" tone={currentPage === 'bands' ? 'white' : 'darkGrey'} />,
                label: 'Bands',
              },
              {
                key: 'musicians',
                icon: <UserCircleIcon size="m" tone={currentPage === 'musicians' ? 'white' : 'darkGrey'} />,
                label: 'Musicians',
              },
              {
                key: 'venues',
                icon: <LocationMarkerIcon size="m" tone={currentPage === 'venues' ? 'white' : 'darkGrey'} />,
                label: 'Venues',
              },
              {
                key: 'connections',
                icon: <LinkIcon size="m" tone={currentPage === 'connections' ? 'white' : 'darkGrey'} />,
                label: 'Connections',
              },
              {
                key: 'profile',
                icon: <UserCircleIcon size="m" tone={currentPage === 'profile' ? 'white' : 'darkGrey'} />,
                label: 'My Account',
              },
            ]}
          />

          {!collapsed && (
            <div
              className="sider-footer-buttons"
              style={{ padding: `${token.paddingXL}px ${token.paddingXL}px` }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size={20}>
                {isAuthenticated ? (
                  <>
                    <div className="sider-cta-card">
                      <p className="sider-cta-card-text">Add Show with <span className="sider-cta-chat-link" onClick={() => { setCollapsed(true); navigate('/chat'); }}><SunglassLogo active style={{ width: 23, height: 8, verticalAlign: 'middle' }} /> <b>Chat</b></span> or below</p>
                    </div>
                    <AccentButton block size="large" shape="round" icon={<CalendarIcon size="m" tone="white" />} onClick={() => { setCollapsed(true); setTimeout(() => navigate('/add-show'), 250); }}>
                      Add Show
                    </AccentButton>
                    <AccentButton block size="large" shape="round" icon={<UserGroupIcon size="m" tone="white" />} onClick={() => { setCollapsed(true); setTimeout(() => navigate('/add-band'), 250); }}>
                      Add Band
                    </AccentButton>
                    {isAdmin && (
                      <AccentButton block size="large" shape="round" icon={<CogIcon size="m" tone="white" />} onClick={() => { setCollapsed(true); setTimeout(() => navigate('/admin/users'), 250); }}>
                        User Management
                      </AccentButton>
                    )}
                    <AccentButtonDark block size="large" shape="round" onClick={async () => { setCollapsed(true); await signOut(); setTimeout(() => navigate('/'), 250); }}>
                      Sign Out
                    </AccentButtonDark>
                    <AccentButtonDark block size="large" shape="round" onClick={() => { setCollapsed(true); setTimeout(() => navigate('/about'), 250); }}>
                      About
                    </AccentButtonDark>
                  </>
                ) : (
                  <>
                    <AccentButton block size="large" shape="round" onClick={() => { setCollapsed(true); setTimeout(() => navigate('/signup', { state: { accountType: 'musician' } }), 250); }}>
                      Join as Musician
                    </AccentButton>
                    <AccentButton block size="large" shape="round" onClick={() => { setCollapsed(true); setTimeout(() => navigate('/signup', { state: { accountType: 'fan' } }), 250); }}>
                      Join as Fan
                    </AccentButton>
                    <AccentButtonDark block size="large" shape="round" onClick={() => { setCollapsed(true); navigate('/signin'); }}>
                      Sign In
                    </AccentButtonDark>
                  </>
                )}
                <AccentButtonDark block size="large" shape="round" onClick={() => { setCollapsed(true); setTimeout(() => navigate('/contact'), 250); }}>
                  Contact
                </AccentButtonDark>
                {user && (
                  <AccentButtonDark block size="large" shape="round" onClick={() => { setCollapsed(true); setTimeout(() => navigate('/feedback'), 250); }}>
                    Feedback
                  </AccentButtonDark>
                )}
              </Space>

              <div className="sider-made-in">
                <h6 className="sider-made-in__text">Made in Tulsa, Oklahoma</h6>
                <a href="https://digitalranch.co" target="_blank" rel="noopener noreferrer" className="sider-made-in__link">
                  <img src="/images/digitalranch-logo.svg" alt="Digital Ranch" className="sider-made-in__logo" />
                </a>
              </div>
            </div>
          )}
        </Sider>

        {/* Backdrop overlay */}
        {!collapsed && (
          <div
            onClick={() => setCollapsed(true)}
            className="backdrop-overlay"
            style={{
              background: token.colorBgSpotlight,
              backdropFilter: `blur(${blur.sm})`,
              WebkitBackdropFilter: `blur(${blur.sm})`,
            }}
          />
        )}

        <Layout style={{ background: token.colorBgLayout }}>
          <Header className={`main-header ${!headerConfig.showGradient ? 'header-no-gradient' : ''}`}>
            <div
              className="header-container"
              style={{
                height: token.controlHeightLG,
                margin: `${token.marginLG}px ${token.padding}px`,
              }}
            >
              <div className="header-container__left">
                {headerConfig.showHamburger && (
                  <IconButton
                    aria-label={collapsed ? 'Open sidebar' : 'Close sidebar'}
                    onClick={() => setCollapsed(!collapsed)}
                    icon={<SunglassLogo active />}
                  />
                )}
                {headerConfig.showClose && (
                  <IconButton
                    aria-label="Close"
                    icon={<XIcon size="m" tone="white" />}
                  />
                )}
                {headerConfig.showBack && (
                  <IconButton
                    aria-label="Back"
                    icon={<ChevronLeftIcon size="m" tone="white" />}
                    onClick={handleBackClick}
                  />
                )}
                {headerConfig.showLogo && (
                  <div onClick={() => navigate('/home')} style={{ cursor: 'pointer' }} className="header-logo-wrapper">
                    <SpecLogo />
                  </div>
                )}
              </div>

              {headerConfig.showSearch && (
                <div 
                  className="search-bar-container"
                  onClick={() => {
                    if (currentPage === 'home') {
                      handleSearchClick();
                    }
                  }}
                  style={{ width: '100%', maxWidth: '768px', cursor: currentPage === 'home' ? 'pointer' : 'text' }}
                >
                  <Input
                    ref={searchInputRef}
                    size="large"
                    suffix={<SpecLogo />}
                    placeholder={currentPage === 'home' ? 'Live Music in Tulsa' : 'Search'}
                    className="header-search-input"
                    style={{ width: '100%' }}
                    value={currentPage === 'home' ? '' : searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentPage === 'home') {
                        handleSearchClick();
                      }
                    }}
                    readOnly={currentPage === 'home'}
                  />
                </div>
              )}

              {(rightExtra || headerConfig.showMore || (headerConfig.showHamburger && isHomePage)) && (
                <div className="header-container__right">
                  {rightExtra}
                  {headerConfig.showMore && (
                    <IconButton
                      aria-label="More"
                      icon={<DotsVerticalIcon size="m" tone="white" />}
                      onClick={handleMoreClick}
                    />
                  )}
                  {headerConfig.showHamburger && isHomePage && (
                    <IconButton
                      aria-label="Open shows drawer"
                      onClick={() => setRightDrawerOpen(true)}
                      icon={<MenuIcon size="m" tone="white" />}
                      style={{ opacity: rightDrawerOpen ? 0 : 1, pointerEvents: rightDrawerOpen ? 'none' : 'auto' }}
                    />
                  )}
                </div>
              )}
            </div>
          </Header>
          <MiniPlayer />
          <Content
            className="main-content"
            style={{
              margin: `0px auto`,
              // padding: token.padding,
              background: token.colorBgLayout,
              color: token.colorText,
            }}
          >
            <ScrollToTop />
            <Outlet />
          </Content>
        </Layout>
        {showFooter && <FooterNav />}
        {isHomePage && (
          <ShowsDrawer
            open={rightDrawerOpen}
            onClose={() => setRightDrawerOpen(false)}
            shows={shows}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            availableDates={availableDates}
            onFocusedShowChange={setFocusedDrawerShow}
          />
        )}
      </Layout>
    </HeaderContext.Provider>
  );
}

export default MainLayout;
